<script lang="ts">
  import CartContactInfo from "./components/cart/CartContactInfo.svelte";
  import ClearCart from "./components/cart/ClearCart.svelte";
  import { cart, cartEmail } from "./services/cart-service";
  import type {Order, OrderResponseV2} from "./models/order.interface";
  // the cart is a list of orders
  // old devs were using this variable for a reason, i'm not sure how/if we need to use this

  let orderSubmitted: Promise<OrderResponseV2>;

  const isLoading = false;
  let orderSubmitting = false;
  let validation: boolean = true;

  // Functions to manually dismiss notifications
  function dismissSuccessNotification() {
    orderSubmitted = null;
  }

  function dismissErrorNotification() {
    validation = true;
  }

  // Format option labels using a dictionary approach with fallback
  function formatToReadableLabel(text: string): string {
    if (!text) return "";

    // Dictionary for common terms - add more as needed
    const dictionary = {
      enddate: "END DATE",
      startdate: "START DATE",
      includedocumentation: "INCLUDE DOCUMENTATION",
      filename: "FILE NAME",
      filetype: "FILE TYPE",
      datatypes: "DATA TYPES",
      includeattributes: "INCLUDE ATTRIBUTES",
      includestationlocation: "INCLUDE STATION LOCATION",
      includestationname: "INCLUDE STATION NAME"
    };

    // Look up in dictionary (case insensitive)
    const key = text.toLowerCase();
    if (dictionary[key]) {
      return dictionary[key];
    }

    // Fallback: Handle camelCase and return uppercase
    return text.replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase();
  }

  async function postOrder() {
    orderSubmitting = true;
    try {
      let submitOrder: Order = {
        email: $cartEmail,
        orders: $cart.map((order) => {
          order.email = $cartEmail;
          return order;
        })
      };

      const response = await fetch(`${process.env.ORDER_URL}/v2`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json"
        },
        body: JSON.stringify(submitOrder)
      });
      if (!response.ok) throw new Error("Failed to submit order");
      orderSubmitted = response.json()
        .then((o: OrderResponseV2) => {
          cart.clear();
          validation = true;
          return o;
        })
    } catch (error) {
      console.log(error);
      validation = false;
      throw error;
    } finally {
      orderSubmitting = false;
    }
  }

</script>

<div class="container-fluid">
  <div class="row">
    <main class="col-md-8">
      <div class="row">
        <div class="col pt-3 ps-3">
          <div>
            <h2>Cart & Order Review</h2>
          </div>
          <div>
            <p class="lead">
              Start by searching for datasets and adding items to your cart. Review your cart and ordering options.
            </p>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <div class="d-flex justify-content-end">
            <div class="m-1"></div>
            <div class="m-1">
              {#if $cart.length > 0}
                <ClearCart {orderSubmitting} />
              {/if}
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col">
          <section id="cart-items" class="{$cart.length === 0 ? 'd-flex justify-content-center' : ''}">
            {#await orderSubmitted}
              <div class="section-loader">
                <div class="loader-text">
                  <div class="loader-block">Loading&hellip;</div>
                </div>
              </div>
            {:then orderResponse}
              {#if orderResponse?.successful_orders?.length > 0}
                <div
                    class="alert alert-dismissible d-flex align-items-start"
                    role="alert"
                    style="background-color: #007EC6; color: white; border: 1px solid #005a94;"
                >
                  <i
                      class="bi bi-check-circle-fill me-3"
                      style="font-size: 1.5rem; margin-top: 0.125rem;"
                  ></i>
                  <div style="flex: 1;">
                    <h4 class="alert-heading mb-2" style="color: white;">Order Submitted!</h4>
                    <p class="mb-2">
                      <strong>Orders for:</strong>
                      {#each orderResponse?.successful_orders as order, i}
                        {order.datasetName}{#if i !== orderResponse?.successful_orders?.length - 1},{/if}
                      {/each}
                    </p>
                    <p class="mb-3">
                      An email with a link to the requested data will be sent to <strong>{$cartEmail}</strong> shortly.
                    </p>
                    <p>
                      If you do not receive the email within 1-2 hours, please check your spam settings to ensure that all emails
                      can be received.
                    </p>
                    <div class="d-flex">
                      <a
                              href="../order-status"
                              class="btn btn-light mx-1 me-2"
                              role="button"
                              style="border: 1px solid white;"
                      >
                        <i class="bi bi-view-stacked"></i>
                        Check Order Status
                      </a>
                      <a
                              href="../dataset-search"
                              class="btn btn-light mx-1"
                              role="button"
                              style="border: 1px solid white;"
                      >
                        <i class="bi bi-search"></i>
                        Search for More Items
                      </a>
                    </div>
                  </div>
                  <button
                      type="button"
                      class="btn-close"
                      aria-label="Close"
                      style="filter: invert(1);"
                      on:click={dismissSuccessNotification}
                  ></button>
                </div>
              {/if}
            {:catch error}
              <div
                  class="alert alert-danger alert-dismissible d-flex align-items-start"
                  role="alert"
              >
                <i
                    class="bi bi-exclamation-triangle-fill me-3"
                    style="font-size: 1.5rem; margin-top: 0.125rem;"
                ></i>
                <div style="flex: 1;">
                  <h4 class="alert-heading mb-2">Order Submission Failed</h4>
                  <p class="mb-0">
                    There was an error submitting your order. Please try again later or contact
                    support if the problem persists.
                  </p>
                </div>
                <button
                    type="button"
                    class="btn-close"
                    aria-label="Close"
                    on:click={dismissErrorNotification}
                ></button>
              </div>
            {/await}

            {#if $cart.length === 0 && orderSubmitted === null}
              <div
                class="alert d-flex align-items-start"
                role="alert"
                style="background-color: #162e51; color: white; border: 1px solid #0f1f3a;"
              >
                <i class="bi bi-cart me-3" style="font-size: 1.5rem; margin-top: 0.125rem;"></i>
                <div>
                  <h4 class="alert-heading mb-2" style="color: white;">Your cart is empty</h4>
                  <a
                    href="dataset-search.html"
                    class="btn btn-light"
                    role="button"
                    style="border: 1px solid white;"
                  >
                    <i class="bi bi-search"></i>
                    Begin Searching
                  </a>
                </div>
              </div>
            {/if}

            <!-- Cart Cards -->
            {#if $cart.length > 0 && !isLoading}
              <div class="row align-items-center row-cols-1 row-cols-sm-1">
                {#each $cart as order, i}
                  <div class="col-md-9 col mb-3">
                    <div class="card" id="cartCards">
                        <div
                                class="card-header d-flex align-items-center justify-content-between"
                                style="background-color: #F1F3F6; position: relative;"
                        >
                        <h5 class="card-title mb-0 me-5 fw-medium">{order?.datasetName}</h5>
                        <button
                          type="button"
                          class="btn btn-danger d-flex align-items-center mx-1"
                          title="remove {i}"
                          aria-label="Remove from cart"
                          disabled={orderSubmitting}
                          on:click={() => cart.removeItem(i)}

                        >
                          <span class="mx-1" style="font-size: 1.2rem;">×</span> Remove
                        </button>
                      </div>
                      <div class="card-body">
                        <table class="table table-borderless" style="width: 100%; table-layout: fixed;">
                          <tbody>
                            <!-- Display files in a single row -->
                            {#if order.files && order.files.length > 0 && order.format === "pub"}
                              <tr>
                                <td><strong>FILE IDENTIFIER(S)</strong></td>
                                <td>
                                  {order.files.map((file) => file.recId).join(", ")}
                                </td>
                              </tr>
                              {:else if order.files && order.files.length > 0}
                              <tr>
                                <td><strong>FILE NAME(S)</strong></td>
                                <td>
                                  {order.files.map((file) => file.name).join(", ")}
                                </td>
                              </tr>
                            {/if}

                            <!-- Display order options -->
                            {#each order.orderOptions as oo}
                              {#if oo.values.length !== 0 && oo.id !== "files"}
                                <tr>
                                  <td><strong>{formatToReadableLabel(oo.name || oo.id)}</strong></td>
                                  <td>
                                    {#if oo.values.length === 1}
                                      {#if oo.values[0] !== null && typeof oo.values[0] === "object" && oo.values[0].value}
                                        {oo.values[0].value}
                                      {:else}
                                        {oo.values[0]}
                                      {/if}
                                    {:else}
                                      {oo.values
                                        .map((val) =>
                                          typeof val === "object" && val.value ? val.value : val
                                        )
                                        .join(", ")}
                                    {/if}
                                  </td>
                                </tr>
                              {/if}
                            {/each}
                            <tr>
                              <td><strong>FORMAT</strong></td>
                              <td>{order?.format}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                {/each}
              </div>
            {/if}
            <!--                </div>-->
          </section>
        </div>
      </div>
    </main>
    <div class="col gx-0">
      <CartContactInfo {postOrder} {orderSubmitting} />
    </div>
  </div>
</div>

<!-- /container -->

<style>
  .table td {
    padding: 0.5rem 1rem;
  }

  #cartCards {
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  }
</style>
